import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { TherapyModality } from '@/hooks/useTherapyModalities';
import { TherapyModalityStats } from '@/hooks/useTherapyModalityStats';
import TherapyCard from './TherapyCard';
import StageMap from './StageMap';

interface ArchiveGridProps {
  therapiesByStage: { [key: number]: TherapyModality[] };
  visibleStages: number[];
  statsMap?: Map<string, TherapyModalityStats>;
  eventCountsMap?: Map<string, number>;
}

const ArchiveGrid: React.FC<ArchiveGridProps> = ({
  therapiesByStage,
  visibleStages,
  statsMap,
  eventCountsMap,
}) => {
  const { t } = useTranslation('therapyLibrary');
  // Stages 0–1 open by default; later stages start collapsed so the archive
  // opens as a readable map instead of one very long scroll.
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>(() => {
    const init: Record<number, boolean> = {};
    visibleStages.forEach((n) => { init[n] = n >= 2; });
    return init;
  });

  const counts = useMemo(() => {
    const c: Record<number, number> = {};
    visibleStages.forEach((n) => { c[n] = (therapiesByStage[n] || []).length; });
    return c;
  }, [visibleStages, therapiesByStage]);

  const jumpToStage = (n: number) => {
    setCollapsed((prev) => ({ ...prev, [n]: false }));
    requestAnimationFrame(() => {
      document.getElementById(`stage-${n}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const toggle = (n: number) => setCollapsed((prev) => ({ ...prev, [n]: !prev[n] }));
  const allCollapsed = visibleStages.every((n) => collapsed[n]);
  const setAll = (value: boolean) => {
    const next: Record<number, boolean> = {};
    visibleStages.forEach((n) => { next[n] = value; });
    setCollapsed(next);
  };

  const isAvailable = (t: TherapyModality) => {
    const pc = statsMap?.get(t.id)?.practitioner_count || 0;
    const ec = eventCountsMap?.get(t.id) || 0;
    return pc > 0 || ec > 0;
  };

  return (
    <div className="space-y-20">
      <StageMap
        visibleStages={visibleStages}
        counts={counts}
        collapsed={collapsed}
        onJump={jumpToStage}
      />

      {/* Global collapse / expand all */}
      <div className="flex justify-end -mb-12">
        <button
          type="button"
          onClick={() => setAll(!allCollapsed)}
          className="text-[11px] font-sans uppercase tracking-[0.2em] text-stone-600 hover:text-[hsl(var(--primary))] transition-colors"
        >
          {allCollapsed
            ? t('expandAllStages', { defaultValue: 'Expand all stages' })
            : t('collapseAllStages', { defaultValue: 'Collapse all stages' })}
        </button>
      </div>

      {visibleStages.map((stageNumber) => {
        const therapies = therapiesByStage[stageNumber] || [];
        if (therapies.length === 0) return null;
        const isCollapsed = !!collapsed[stageNumber];

        const available = therapies.filter(isAvailable);
        const knowledge = therapies.filter((t) => !isAvailable(t));

        // Sort available by practitioner_count desc, then event count desc
        available.sort((a, b) => {
          const pa = statsMap?.get(a.id)?.practitioner_count || 0;
          const pb = statsMap?.get(b.id)?.practitioner_count || 0;
          if (pb !== pa) return pb - pa;
          const ea = eventCountsMap?.get(a.id) || 0;
          const eb = eventCountsMap?.get(b.id) || 0;
          return eb - ea;
        });

        return (
          <section key={stageNumber} id={`stage-${stageNumber}`} className="relative scroll-mt-40">
            {/* Stage header — editorial, clickable to collapse */}
            <header className="mb-10 border-b border-[hsl(var(--primary)/0.2)] pb-5">
              <button
                type="button"
                onClick={() => toggle(stageNumber)}
                aria-expanded={!isCollapsed}
                className="w-full flex items-end justify-between gap-6 text-left group"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-3">
                    {t('stageLabel', { defaultValue: 'Stage' })} {stageNumber}
                  </p>
                  <h2 className="font-serif italic text-3xl md:text-4xl text-stone-900 tracking-tight group-hover:text-[hsl(var(--primary))] transition-colors">
                    {t(`stages.${stageNumber}.title`)}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm md:text-base text-stone-600 leading-relaxed font-sans">
                    {t(`stages.${stageNumber}.description`)}
                  </p>
                </div>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="hidden md:inline text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sans whitespace-nowrap">
                    {t('therapiesCount', { count: therapies.length })}
                  </span>
                  <span
                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full border border-[hsl(var(--primary)/0.4)] text-stone-600 group-hover:bg-[hsl(var(--primary)/0.1)] group-hover:text-stone-900 transition-all ${isCollapsed ? '' : 'rotate-180'}`}
                    aria-hidden="true"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </span>
                </span>
              </button>
            </header>

            {!isCollapsed && (
              <div className="space-y-12">
                {available.length > 0 && (
                  <div>
                    <div className="flex items-baseline justify-between mb-5">
                      <h3 className="text-[11px] uppercase tracking-[0.25em] font-sans text-stone-700">
                        {t('availableSupport', { defaultValue: 'Available support' })}
                      </h3>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sans">
                        {available.length}
                      </span>
                    </div>
                    <div className="grid gap-8 md:gap-10 md:grid-cols-2">
                      {available.map((therapy) => (
                        <TherapyCard
                          key={therapy.id}
                          therapy={therapy}
                          stats={statsMap?.get(therapy.id)}
                          eventCount={eventCountsMap?.get(therapy.id) || 0}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {knowledge.length > 0 && (
                  <div>
                    <div className="flex items-baseline justify-between mb-5">
                      <h3 className="text-[11px] uppercase tracking-[0.25em] font-sans text-stone-500">
                        {t('relatedKnowledge', { defaultValue: 'Related knowledge' })}
                      </h3>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sans">
                        {knowledge.length}
                      </span>
                    </div>
                    <div className="grid gap-8 md:gap-10 md:grid-cols-2 opacity-90">
                      {knowledge.map((therapy) => (
                        <TherapyCard
                          key={therapy.id}
                          therapy={therapy}
                          stats={statsMap?.get(therapy.id)}
                          eventCount={eventCountsMap?.get(therapy.id) || 0}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default ArchiveGrid;
